import * as React from "react";
import {ReactNode, useCallback, useMemo} from "react";
import { Autocomplete, TextField } from "@mui/material";
import { FormikProps } from "formik";

export interface Option {
    value: string;
    label: string | ReactNode;
}

export interface AbstractAutoCompleteProps {
    options?: Option[];
    value?: string | number | null;
    label: string | ReactNode;
    name?: string;
    isLoading: boolean
    error?: string | ReactNode;
    submitHappen?: boolean;
    disabled?: boolean;
    formik?: FormikProps<any>;
    valueRender?: (value?: string) => string;
    textField?: {
        onChange: (s:string) => void
        value: string
    }
    onChange?: (value: string | null, option: Option | null | undefined, setIsOpen:(b:boolean) => void, reason:"enter-first"|"enter"|"on-change"|"textfield-on-change") => void;
}

export function AutoComplete({
    isLoading,
                                         disabled,
                                         name,
                                         submitHappen,
                                         formik,
                                         value,
                                         label,
                                         options = [],
                                         error,
                                 valueRender,
                                        textField,
                                         onChange
                                     }: AbstractAutoCompleteProps) {

    const id = useMemo(() => Math.random().toString(), []);
    const [isOpen, setIsOpen] = React.useState(false);

    if (formik && !name) {
        alert("name not defined, while formik defined");
    }

    const formikValue =
        formik && name ? formik.values[name] : undefined;

    const realValue = value !== undefined ? value : formikValue;

    const resolvedValue = useMemo(() => {
        return value !== undefined
            ? options.find(o => o.value === value) || null
            : options.find(o => o.value === formikValue) || null;
    }, [value, formikValue, options]);

    const optionsToRender = useMemo(() => {
        if(!resolvedValue) {
            if(realValue) {
                console.log("added option: " + realValue)
                return [...options, {value: realValue, label: realValue}];
            }
        }
        return options;
    }, [realValue, resolvedValue, options]);

    const secondTimeResolvedValue = useMemo(() => {
        if(resolvedValue) {
            return resolvedValue;
        }
        return realValue ? optionsToRender[optionsToRender.length - 1] || null : null;
    }, [realValue, resolvedValue, optionsToRender]);


    const errorToDisplay =
        formik
            ? (submitHappen || formik.touched[name!]) && formik.errors[name!]
            : error;

    const isError =
        formik
            ? (submitHappen || formik.touched[name!]) && Boolean(formik.errors[name!])
            : Boolean(error);

    const setValue = useCallback((option:Option|null, reason:"enter-first"|"enter"|"on-change"|"textfield-on-change") => {
        const newValue = option ? option.value : null;
        if (onChange) {
            onChange(newValue, option, setIsOpen, reason);
        } else if (formik && name) {
            formik.setFieldValue(name, newValue);
        }
    }, [onChange, name, formik])

    return (<>
    {/*<div>Value: {value}</div>
            {formik && name && <div>Formik Value: {formik.values[name]}</div>}
            <div>Resolved Value: {JSON.stringify(secondTimeResolvedValue)} {secondTimeResolvedValue?.value}</div>
            <div>Textfield Value: {(textField?.value)}</div>*/}
        <Autocomplete
            id={id}
            open={isOpen}
            loading={isLoading}
            disabled={disabled}
            inputValue={(secondTimeResolvedValue && secondTimeResolvedValue.label) || (textField?.value || "")}
            onInputChange={(_, newInputValue, reason) => {
                if(reason === 'reset') {
                    return;
                }
                textField?.onChange(newInputValue);
                setIsOpen(true);
                setValue(null, "textfield-on-change");
            }}
            options={optionsToRender}
            value={secondTimeResolvedValue}
            getOptionLabel={(option) =>
                typeof option.label === "string" ? option.label : ""
            }
            isOptionEqualToValue={(option, value) =>
                option.value === value.value
            }
            onKeyDown={e => {
                if(e.code === 'Enter' && textField?.value) {
                    e.preventDefault();
                    if(options?.length === 1 && options[0].value.toLowerCase() === textField.value.toLowerCase()) {
                        setValue(options[0], "enter-first")
                        return;
                    }
                    for(let i = 0; i < (options || []).length; ++i) {
                        if(options[i].value === textField.value) {
                            setValue(options[i], "enter")
                            break;
                        }
                    }
                }
            }}
            onChange={(_, option) => {
                setValue(option, 'on-change')
            }}
            renderInput={(params) => {
                return <TextField
                    {...params}
                    onFocus={() => setIsOpen(true)}
                    onBlur={() => setIsOpen(false)}
                    label={label}
                    name={name}
                    variant="outlined"
                    error={isError}
                    helperText={errorToDisplay as ReactNode}
                />
            }}
            fullWidth
        />
        </>
    );
}